import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  effect,
  EffectRef,
  ElementRef,
  inject,
  linkedSignal,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { EstimateurEtape, EstimatorOption, OptionSelectedType } from '@components/estimateur-etape/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { OptionCardSelectComponent } from '@shared/components/option-card-select';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { SomeByPropertyPipe } from '@shared/pipe';
import { LanguageService } from '@shared/services/language';
import { ResponsiveService } from '@shared/services/responsive';
import { UrlParamsService } from '@shared/services/url-params';
import { getUuid } from '@shared/utils';
import { BudgetEstimatorStore } from '@stores';

@Component({
  selector: 'app-estimateur-etape',
  imports: [
    TitleComponent,
    SvgComponent,
    NgTemplateOutlet,
    SomeByPropertyPipe,
    ButtonComponent,
    OptionCardSelectComponent,
    TranslatePipe,
  ],
  templateUrl: './estimateur-etape.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EstimateurEtapeComponent extends AbstractDynamicComponent<EstimateurEtape> implements OnInit, OnDestroy {
  readonly step1StyleEl = viewChild<ElementRef<HTMLDivElement>>('step1StyleEl');
  readonly step1LayoutEl = viewChild<ElementRef<HTMLDivElement>>('step1LayoutEl');
  readonly step1SizeEl = viewChild<ElementRef<HTMLDivElement>>('step1SizeEl');
  readonly step1WorkPlanEl = viewChild<ElementRef<HTMLDivElement>>('step1WorkPlanEl');
  readonly step2SinkTypeEl = viewChild<ElementRef<HTMLDivElement>>('step2SinkTypeEl');
  readonly step2HouseholdApplianceEl = viewChild<ElementRef<HTMLDivElement>>('step2HouseholdApplianceEl');
  readonly step3InstallationEl = viewChild<ElementRef<HTMLDivElement>>('step3InstallationEl');
  readonly showStep3Clicked = signal(false);
  readonly showStep3 = computed(() => {
    const estimateId = this.estimateId();
    if (estimateId) return true;
    const currentEstimate = this.currentEstimate();
    const showStep2 = this.showStep2() ? true : undefined;
    if (currentEstimate) {
      const { sinkTypeSelected, householdApplianceSelected, householdApplianceSelectedNone } = currentEstimate;
      let householdAppliance = undefined;
      if (
        householdApplianceSelectedNone === true ||
        (householdApplianceSelected && householdApplianceSelected.length > 0)
      ) {
        householdAppliance = true;
      }
      return ![showStep2, sinkTypeSelected, householdAppliance].some((value) => value === undefined);
    }
    return false;
  });
  private readonly budgetEstimatorStore = inject(BudgetEstimatorStore);
  readonly currentEstimate = linkedSignal({
    source: this.budgetEstimatorStore.select('currentEstimate'),
    computation: (newEstimate) => newEstimate ?? {},
  });
  private readonly router = inject(Router);
  private readonly urlParamsService = inject(UrlParamsService);
  readonly estimateId = this.urlParamsService.param('estimate_id');
  readonly showStep2 = computed(() => {
    const estimateId = this.estimateId();
    if (estimateId) return true;
    const currentEstimate = this.currentEstimate();
    if (currentEstimate) {
      const { styleSelected, layoutSelected, sizeSelected, workPlanSelected } = currentEstimate;
      return ![styleSelected, layoutSelected, sizeSelected, workPlanSelected].some((value) => value === undefined);
    }
    return false;
  });
  readonly canGoToEstimateSummary = computed(() => {
    const estimateId = this.estimateId();
    const currentEstimate = this.currentEstimate();
    if (estimateId && currentEstimate) {
      const { householdApplianceSelected, householdApplianceSelectedNone } = currentEstimate;
      let householdAppliance = undefined;
      if (
        householdApplianceSelectedNone === true ||
        (householdApplianceSelected && householdApplianceSelected.length > 0)
      ) {
        householdAppliance = true;
      }
      return currentEstimate?.installationSelected !== undefined && this.showStep3() && householdAppliance;
    } else {
      return currentEstimate?.installationSelected !== undefined && this.showStep3();
    }
  });
  private readonly isMobile = inject(ResponsiveService).isMobile;
  private readonly stepUrl = this.urlParamsService.param('step');
  private readonly languageService = inject(LanguageService);
  readonly optionNone: EstimatorOption = {
    id: -1,
    name: this.languageService.instant('COMPONENT.ESTIMATEUR-ETAPE.I_DONT_NEED_ANYTHING'),
  };
  private readonly goToRecapEffect: EffectRef;
  private readonly stepEffect: EffectRef;
  private readonly window = inject(DOCUMENT).defaultView as Window;

  constructor() {
    super();
    this.goToRecapEffect = effect(() => {
      const estimateId = untracked(this.estimateId);
      if (this.canGoToEstimateSummary() && !estimateId) {
        this.goToRecap();
      }
    });
    this.stepEffect = effect(() => {
      const isEditMode = !!untracked(this.estimateId);
      if (isEditMode) {
        untracked(() => this.budgetEstimatorStore.update('currentStep', 4));
      } else {
        const showStep2 = this.showStep2();
        const showStep3 = this.showStep3Clicked();

        let desiredStep = 1;
        if (showStep3) {
          desiredStep = 3;
        } else if (showStep2) {
          desiredStep = 2;
        }
        const currentStep = this.budgetEstimatorStore.select('currentStep')() ?? 0;

        if (currentStep !== desiredStep) {
          untracked(() => this.budgetEstimatorStore.update('currentStep', desiredStep));
        }
      }
    });
    effect(() => {
      const currentStep = this.budgetEstimatorStore.select('currentStep')();
      if (currentStep === 2) {
        const gtm = this.data.gtmConfigViewStep2;
        this.emitGtmEvent({
          event: gtm?.event,
          group: gtm?.group,
          description: gtm?.description,
          context: gtm?.context,
          event_type: 'view',
        });
      }
      if (currentStep === 3) {
        const gtm = this.data.gtmConfigViewStep3;
        this.emitGtmEvent({
          event: gtm?.event,
          group: gtm?.group,
          description: gtm?.description,
          context: gtm?.context,
          event_type: 'view',
        });
      }
    });
  }

  ngOnInit(): void {
    this.budgetEstimatorStore.update('isBudgetEstimatorPage', true);
    const stepUrl = this.stepUrl() as OptionSelectedType;
    const estimateId = this.estimateId();
    if (!estimateId) {
      this.currentEstimate.set({});
    } else {
      const gtm2 = this.data.gtmConfigViewStep2;
      this.emitGtmEvent({
        event: gtm2?.event,
        group: gtm2?.group,
        description: gtm2?.description,
        context: gtm2?.context,
        event_type: 'view',
      });
    }
    const styleSelected = this.currentEstimate()?.styleSelected;
    if (!this.currentEstimate()._id) {
      this.currentEstimate.update((state) => ({ ...state, _id: getUuid() }));
    }
    if (estimateId && styleSelected) {
      this.showStep3Clicked.set(true);
      const gtm3 = this.data.gtmConfigViewStep3;
      this.emitGtmEvent({
        event: gtm3?.event,
        group: gtm3?.group,
        description: gtm3?.description,
        context: gtm3?.context,
        event_type: 'view',
      });
    }
    if (stepUrl) {
      this.scrollToStep(stepUrl, true);
    }
  }

  ngOnDestroy(): void {
    this.budgetEstimatorStore.update('isBudgetEstimatorPage', false);
  }

  onOptionSelected(type: OptionSelectedType, option: EstimatorOption) {
    const isEditMode = !!this.estimateId();
    switch (type) {
      case 'style':
        this.currentEstimate.update((state) => ({ ...state, styleSelected: { ...option, _step: type } }));
        this.scrollToStep(isEditMode ? 'installation' : 'layout');
        break;
      case 'layout':
        this.currentEstimate.update((state) => ({ ...state, layoutSelected: { ...option, _step: type } }));
        this.scrollToStep(isEditMode ? 'installation' : 'size');
        break;
      case 'size':
        this.currentEstimate.update((state) => ({ ...state, sizeSelected: { ...option, _step: type } }));
        this.scrollToStep(isEditMode ? 'installation' : 'workPlan');
        break;
      case 'workPlan':
        this.currentEstimate.update((state) => ({ ...state, workPlanSelected: { ...option, _step: type } }));
        this.scrollToStep(isEditMode ? 'installation' : 'sinkType');
        break;
      case 'sinkType':
        this.currentEstimate.update((state) => ({ ...state, sinkTypeSelected: { ...option, _step: type } }));
        this.scrollToStep(isEditMode ? 'installation' : 'householdAppliance');
        break;
      case 'householdAppliance':
        this.checkHouseAppliance({ ...option, _step: type });
        break;
      case 'installation':
        this.currentEstimate.update((state) => ({ ...state, installationSelected: { ...option, _step: type } }));
        break;
    }
  }

  goToRecap(): void {
    this.goToRecapEffect.destroy();
    this.stepEffect.destroy();
    const estimateId = this.estimateId();
    const currentEstimate = this.currentEstimate();
    this.budgetEstimatorStore.update('currentEstimate', this.currentEstimate());
    const recentEstimates = this.budgetEstimatorStore.select('recentEstimates')() ?? [];
    if (estimateId && recentEstimates.length > 0) {
      this.budgetEstimatorStore.update(
        'recentEstimates',
        recentEstimates.map((estimate) => (estimate._id === estimateId ? currentEstimate : estimate))
      );
    } else {
      this.budgetEstimatorStore.update('recentEstimates', [...recentEstimates, this.currentEstimate()]);
    }
    this.budgetEstimatorStore.update('currentStep', 4);
    void this.router.navigate([this.data.cta.url]);
  }

  handleGoToStep3(buttonLabel: string): void {
    this.showStep3Clicked.set(true);
    const gtm = this.data.gtmConfigViewStep3Btn;
    this.emitGtmEvent({
      event: gtm?.event,
      group: gtm?.group,
      description: gtm?.description,
      context: gtm?.context,
      button_text_value: buttonLabel,
      event_type: 'click',
    });
    this.scrollToStep('installation');
  }

  private scrollToStep(type: OptionSelectedType, fromStepUrl = false): void {
    const headerOffset = this.isMobile() ? 150 : 120;
    setTimeout(
      () => {
        let element: HTMLElement | undefined;
        switch (type) {
          case 'style':
            element = this.step1StyleEl()?.nativeElement;
            break;
          case 'layout':
            element = this.step1LayoutEl()?.nativeElement;
            break;
          case 'size':
            element = this.step1SizeEl()?.nativeElement;
            break;
          case 'workPlan':
            element = this.step1WorkPlanEl()?.nativeElement;
            break;
          case 'sinkType':
            element = this.step2SinkTypeEl()?.nativeElement;
            break;
          case 'householdAppliance':
            element = this.step2HouseholdApplianceEl()?.nativeElement;
            break;
          case 'installation':
            element = this.step3InstallationEl()?.nativeElement;
            break;
        }

        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;

          this.window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      },
      fromStepUrl ? 500 : 0
    );
  }

  private checkHouseAppliance(option: EstimatorOption): void {
    const currentEstimate = this.currentEstimate();
    const householdApplianceSelected = currentEstimate.householdApplianceSelected ?? [];
    const householdApplianceSelectedNone = currentEstimate.householdApplianceSelectedNone ?? false;
    if (option.id === -1) {
      if (householdApplianceSelectedNone) {
        this.currentEstimate.update((state) => ({ ...state, householdApplianceSelectedNone: false }));
      } else {
        this.currentEstimate.update((state) => {
          delete state.householdApplianceSelected;
          return { ...state, householdApplianceSelectedNone: true };
        });
      }
    } else {
      const index = householdApplianceSelected.findIndex((item) => item.id === option.id);
      let newSelection: EstimatorOption[];
      if (index > -1) {
        newSelection = householdApplianceSelected.filter((item) => item.id !== option.id);
      } else {
        newSelection = [...householdApplianceSelected, option];
      }
      this.currentEstimate.update((state) => ({
        ...state,
        householdApplianceSelectedNone: false,
        householdApplianceSelected: newSelection,
      }));
    }
  }
}
